import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FlatList, FlatListProps, StyleSheet, useWindowDimensions, ViewStyle } from 'react-native';
import { useLocation } from 'react-router';
import { RecyclerListView, RecyclerListViewProps, DataProvider, LayoutProvider } from 'recyclerlistview';
import {
  GridList,
  DNABox,
  DNACard,
  DNADivider,
  DNACheckbox,
  Iffy,
  luxColors,
  DNAText,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import useFixedHeader from '@alucio/lux-ui/src/hooks/useFixedLayout';
import {
  BindDocumentContextActions,
  DEFAULT_PAGE_SIZE,
  DNAEmpty,
  EmptyVariant,
  ListFieldConfig,
  getFieldConfigsForTenant,
  usePagination,
  useDNADocumentActions,
  DocumentSearchResultRow,
  GridListColumnHeaderRow,
  DNADocumentListRow,
} from 'src/components/DNA';
import ROUTES from 'src/router/routeDef';
import { Variant, DocumentORM } from 'src/types/types';
import { SelectedDocuments } from 'src/screens/MyLibrary/DocumentSelectManager';
import { useAllDocumentsLength } from 'src/state/redux/selector/document';
import { useUserTenant } from 'src/state/redux/selector/user';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import { useDocumentSearchV2Context } from 'src/hooks/useDocumentSearchV2';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import {
  getDocumentSummariesFromContentPageDataCache,
  getS3KeysWithinDocumentVersionORM,
} from 'src/hooks/useContentPageData/useContentPageData';
import { fetchPagesJson } from 'src/utils/loadCloudfrontAsset/common';
import {
  getDocumentSummariesByVersion,
  getDocumentSummariesFromVersions,
  getTabletDocumentRows,
  getUncachedDocumentSummaryPageJsonKeys,
  hasTabletDocumentRowChanged,
  supportsDocumentSummary,
  TabletDocumentRow,
} from 'src/components/DNA/Document/DNADocumentSummary.utils';

export interface DNADocumentListProps {
  documents: DocumentORM[],
  emptyVariant?: EmptyVariant,
  selectEnabled?: boolean,
  selectedDocuments?: SelectedDocuments,
  onSelectDocuments?: (documents: DocumentORM[]) => void,
  isSearch?: boolean,
  filtersVisible?: boolean,
  pageNumber?: number,
  showDocumentSummaries?: boolean,
}
export interface DNADocumentListComponent extends React.FC<DNADocumentListProps> {
  Paginated: typeof DNADocumentListPaginated
}

type ExtendedState = {
  documentActions: BindDocumentContextActions,
  selectedDocuments?: SelectedDocuments,
  selectEnabled?: boolean,
  fieldConfigs: ListFieldConfig[],
  isOnline: boolean,
  isDesktop: boolean,
  onThumbnailPress: (documentORM) => void,
  onSelectDocument: (documentORM) => void | undefined,
  documentSummaries: Record<string, string>,
}

type SharedExtendedState = Omit<ExtendedState, 'documentSummaries'>;

const DOCUMENT_SUMMARY_FIELD: ListFieldConfig = {
  dataType: 'string',
  fieldLabel: 'Summary',
  fieldName: 'documentSummary',
  fieldType: 'MODEL',
  width: 280,
};

const useDocumentSummaries = (
  documents: DocumentORM[],
  enabled: boolean,
): Record<string, string> => {
  const [documentSummaries, setDocumentSummaries] = useState<Record<string, string>>({});
  const summaryVersions = useMemo(
    () => documents.map(document => document.relations.version.latestUsableDocumentVersionORM)
      .filter(version => supportsDocumentSummary(version.model.type)),
    [documents],
  );
  const pageJsonKeys = useMemo(
    () => getS3KeysWithinDocumentVersionORM(summaryVersions),
    [summaryVersions],
  );
  const documentSummariesFromVersions = useMemo(
    () => getDocumentSummariesFromVersions(summaryVersions.map(version => ({
      id: version.model.id, shortSummary: version.model.shortSummary,
    }))),
    [summaryVersions],
  );

  useEffect(() => {
    let isMounted = true;

    if (!enabled) {
      setDocumentSummaries({});
      return () => { isMounted = false; };
    }

    if (Object.keys(pageJsonKeys).length === 0) {
      setDocumentSummaries(documentSummariesFromVersions);
      return () => { isMounted = false; };
    }

    const cachedDocumentSummaries = getDocumentSummariesFromContentPageDataCache(Object.keys(pageJsonKeys));
    const cachedSummaries = getDocumentSummariesByVersion(
      Object.entries(cachedDocumentSummaries).map(([documentVersionId, shortSummary]) => ({
        documentVersionId,
        content: { shortSummary },
      })),
    );
    const uncachedPageJsonKeys = getUncachedDocumentSummaryPageJsonKeys(
      pageJsonKeys,
      { ...cachedDocumentSummaries, ...documentSummariesFromVersions },
    );

    setDocumentSummaries({ ...cachedSummaries, ...documentSummariesFromVersions });

    if (Object.keys(uncachedPageJsonKeys).length === 0) {
      return () => { isMounted = false; };
    }

    fetchPagesJson(uncachedPageJsonKeys)
      .then(responses => {
        if (isMounted) {
          setDocumentSummaries({
            ...cachedSummaries,
            ...getDocumentSummariesByVersion(responses),
            ...documentSummariesFromVersions,
          });
        }
      })
      .catch(() => {
        // Keep summaries already available in the in-memory Pages.json cache.
      });

    return () => { isMounted = false; };
  }, [enabled, pageJsonKeys, documentSummariesFromVersions]);

  return documentSummaries;
};

const S = StyleSheet.create({
  DesktopContainer: {
    backgroundColor: colors['color-white'],
    overflow: 'hidden',
    borderRadius: 8,
  },
  MobileContainer: {
    backgroundColor: luxColors.info.primary,
  },
  DataCell: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  flex: {
    flex: 1,
  },
})

// Temp fix until the horizonatel scroll behaivor for the full app is implemente
// per my understanding eng and product are working on having a ux approach for all the app
const ConditionalScrollView: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { deviceMode } = useAppSettings()
  const isDesktop = deviceMode === 'desktop';

  if (isDesktop) {
    return (
      <DNABox
        style={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '60dvh',
          width: '100%',
        } as ViewStyle}
      >
        <DNABox style={{ minWidth: 'max-content' }}>
          {children}
        </DNABox>
      </DNABox>
    )
  }
  return <>{children}</>
}

// [TODO] - Consider cleaning/breaking this up
export const DNADocumentGridList: DNADocumentListComponent = (props) => {
  const {
    documents,
    emptyVariant,
    selectEnabled,
    selectedDocuments,
    onSelectDocuments,
    isSearch,
    filtersVisible,
    showDocumentSummaries: documentSummariesRequested = false,
  } = props;
  const enableDocumentSummaries = useFeatureFlags('enableDocumentSummaries');
  const showDocumentSummaries = documentSummariesRequested && enableDocumentSummaries;
  const allDocumentsLength = useAllDocumentsLength()
  const userTenant = useUserTenant()
  const { deviceMode } = useAppSettings()
  const isDesktop = deviceMode === 'desktop';
  const { pathname } = useLocation();
  const isBookmarkView = (
    pathname.toLowerCase() === ROUTES.UI2_FAVORITE_FILES.PATH.toLowerCase() ||
    pathname.toLowerCase().startsWith('/favorites/files')
  );
  const variant = isBookmarkView ? Variant.bookmark : Variant.MSL;
  const fieldConfigs = useMemo(
    () => {
      const fields = getFieldConfigsForTenant(userTenant, variant, isDesktop, selectEnabled);
      const titleIndex = fields.findIndex(field => field.fieldName === 'title');

      return showDocumentSummaries && titleIndex >= 0
        ? [
          ...fields.slice(0, titleIndex + 1),
          DOCUMENT_SUMMARY_FIELD,
          ...fields.slice(titleIndex + 1),
        ]
        : fields;
    },
    [userTenant, isDesktop, variant, selectEnabled, showDocumentSummaries],
  );
  const documentActions = useDNADocumentActions()
  const { isOnline } = useAppSettings()
  const fixedHeaderWidth = useFixedHeader();
  const { documentIdSnippetMap, onPreviewDocumentFromSearchResult } = useDocumentSearchV2Context();
  const documentSummaries = useDocumentSummaries(documents, showDocumentSummaries && !isSearch);
  const dimensions = useWindowDimensions();
  const onSelectDocument = useCallback(
    (doc: DocumentORM) => props.onSelectDocuments?.([doc]),
    [props.onSelectDocuments],
  );
  const onThumbnailPress = useCallback(
    (documentORM: DocumentORM) => documentActions.present(documentORM)(),
    [],
  );

  const initialNumToRender = DEFAULT_PAGE_SIZE;

  const deviceModeProps = isDesktop
    ? { style: S.DesktopContainer }
    : { fill: true, style: S.MobileContainer }

  const hasFilterDocumentResults = allDocumentsLength && documents.length;

  const cols = useMemo(
    () => fieldConfigs
      .reduce(
        (acc, field) => (
          (field.fieldType === 'ACTION_MENU' || hasFilterDocumentResults)
            ? `${acc} ${field.width}px`
            : acc
        ),
        '',
      ),
    [fieldConfigs, hasFilterDocumentResults],
  )

  const allDocumentSelected = useMemo(
    () => {
      if (!selectedDocuments) return
      return documents.every(doc => selectedDocuments[doc.model.id])
    },
    [selectedDocuments, documents],
  )

  const selectAllDocuments = useCallback(
    () => { onSelectDocuments?.(documents) },
    [onSelectDocuments, documents],
  )

  const keyExtractor = useCallback(
    (documentORM, index) => `${documentORM.model.id}-${index}`,
    [],
  )

  const sharedExtendedState = useMemo<SharedExtendedState>(
    () => ({
      documentActions,
      selectedDocuments,
      isOnline,
      isDesktop,
      onThumbnailPress,
      fieldConfigs,
      selectEnabled,
      onSelectDocument,
    }),
    [
      documentActions,
      selectedDocuments,
      isOnline,
      isDesktop,
      onThumbnailPress,
      fieldConfigs,
      selectEnabled,
      onSelectDocument,
    ],
  )
  const desktopExtendedState = useMemo<ExtendedState>(() => ({
    ...sharedExtendedState,
    documentSummaries,
  }), [sharedExtendedState, documentSummaries]);
  const tabletExtendedState = useMemo<ExtendedState>(() => ({
    ...sharedExtendedState,
    documentSummaries: {},
  }), [sharedExtendedState]);

  const renderItemFactory = useCallback((
    documentORM: DocumentORM,
    extendedState: ExtendedState,
    idx: number,
    deviceMode: DeviceMode,
  ) => {
    const onPress = extendedState.documentActions.preview(documentORM)

    return (
      <>
        {idx > 0 && deviceMode === 'desktop' && <DNADivider />}
        <DNADocumentListRow
          isOnline={extendedState.isOnline}
          isDesktop={extendedState.isDesktop}
          documentORM={documentORM}
          variant={Variant.MSL}
          onPress={onPress}
          onThumbnailPress={extendedState.onThumbnailPress}
          fields={extendedState.fieldConfigs}
          cellStyle={S.DataCell}
          selectEnabled={extendedState.selectEnabled}
          checked={!!extendedState.selectedDocuments?.[documentORM.model.id]}
          onCheck={extendedState.onSelectDocument}
          documentSummaries={extendedState.documentSummaries}
        />
        {deviceMode === 'tablet' && <DNADivider />}
      </>
    )
  }, [])

  // [NOTE] - extraData in <FlatList> does inject extended data, only refreshes the render
  const flatListRenderItem = useCallback(({ item: documentORM, index }: { item: DocumentORM, index: number }) => {
    return renderItemFactory(documentORM, desktopExtendedState, index, DeviceMode.desktop)
  }, [desktopExtendedState])

  const recylerListViewRenderItem = useCallback<RecyclerListViewProps['rowRenderer']>(
    (type, item: TabletDocumentRow<DocumentORM>, idx, extendedState) => {
      return renderItemFactory(item.document, {
        ...(extendedState as ExtendedState),
        documentSummaries: item.documentSummaries,
      }, idx, DeviceMode.tablet)
    },
    [renderItemFactory],
  )

  const renderSearchItem = useCallback<NonNullable<FlatListProps<DocumentORM>['renderItem']>>(
    ({ item: documentORM }) => {
      const snippet = documentIdSnippetMap.get(documentORM.model.id);
      const checked = !!(
        desktopExtendedState.selectedDocuments &&
        desktopExtendedState.selectedDocuments[documentORM.model.id]
      );
      const onPress = (): void => {
        const documentVersionId = documentORM.relations.version.latestUsableDocumentVersionORM.model.id;
        onPreviewDocumentFromSearchResult(documents, documentVersionId);
        desktopExtendedState.documentActions.preview(documentORM, snippet?.pageNumber)();
      };

      return (
        <DocumentSearchResultRow
          documentORM={documentORM}
          selectEnabled={selectEnabled}
          checked={checked}
          onCheck={() => desktopExtendedState.onSelectDocument(documentORM)}
          onPress={onPress}
        />
      )
    },
    [
      documentIdSnippetMap,
      desktopExtendedState,
    ],
  )

  const tabletDocumentRows = useMemo(
    () => getTabletDocumentRows(documents, documentSummaries),
    [documents, documentSummaries],
  );
  const dataProvider = useMemo(
    // [NOTE] - Save some resources by only initializing in Tablet mode
    // [NOTE] - There is an alternative optimization path by using refs during item selection
    //          and determining whether or not to selectively render rows in DataProviders should render function
    //          by using the ref to force update, which should be selective
    () => !isDesktop
      ? new DataProvider(hasTabletDocumentRowChanged)
        .cloneWithRows(tabletDocumentRows)
      : undefined,
    [isDesktop, tabletDocumentRows],
  );

  const layoutProvider = useMemo(
    () => new LayoutProvider(
      () => 'DEFAULT',
      (itemType, dim) => {
        dim.width = 2000;
        dim.height = 90;
      },
    ),
    [],
  )

  const searchContainerStyle = useMemo(() => ({
    width: isDesktop ? fixedHeaderWidth - (filtersVisible ? 400 : 0) : undefined,
    flex: isDesktop ? undefined : 1,
  }), [isDesktop, fixedHeaderWidth, filtersVisible]);

  if (isSearch) {
    return (
      <DNABox
        fill
        testID={!isDesktop ? 'search-mobile-container' : 'search-desktop-container'}
      >
        <DNACard appearance="outline" testID={!isDesktop ? 'search-mobile-card' : undefined}>
          <Iffy is={selectEnabled}>
            <DNABox appearance="col" testID={!isDesktop ? 'search-mobile-select-col' : undefined}>
              <DNABox alignY="center" style={{ paddingLeft: 24, paddingVertical: 16 }}>
                <DNACheckbox
                  context="altBg"
                  checked={allDocumentSelected}
                  onChange={onSelectDocuments ? () => onSelectDocuments(documents) : undefined}
                />
                <DNAText bold style={{ marginLeft: 16 }}>
                  Select All
                </DNAText>
              </DNABox>
              <DNADivider />
            </DNABox>
          </Iffy>
          <DNABox
            style={searchContainerStyle}
            testID={!isDesktop ? 'search-mobile-list-wrapper' : 'search-desktop-list-wrapper'}
          >
            <FlatList
              data={documents}
              keyExtractor={keyExtractor}
              initialNumToRender={initialNumToRender}
              extraData={desktopExtendedState}
              renderItem={renderSearchItem}
              ItemSeparatorComponent={DNADivider}
              ListEmptyComponent={<DNAEmpty emptyVariant={EmptyVariant.DocumentListEmptySearch} />}
              testID={!isDesktop ? 'search-mobile-flatlist' : 'search-desktop-flatlist'}
            />
          </DNABox>
        </DNACard>
      </DNABox>
    )
  }

  return (
    <DNABox fill testID={!isDesktop ? 'documentlist-mobile-container' : 'documentlist-desktop-container'}>
      <Iffy is={!hasFilterDocumentResults}>
        <DNAEmpty emptyVariant={emptyVariant || EmptyVariant.DocumentListEmpty} />
      </Iffy>
      <Iffy is={hasFilterDocumentResults}>
        <ConditionalScrollView>
          <GridList testID="documentlist-gridlist" cols={cols} gap="12px">
            <GridListColumnHeaderRow
              fieldConfigs={fieldConfigs}
              selectEnabled={selectEnabled}
              allDocumentSelected={allDocumentSelected}
              selectAllDocuments={selectAllDocuments}
            />
            <DNABox {...deviceModeProps}>
              {/* Document Rows Desktop */}
              <Iffy is={isDesktop}>
                <FlatList
                  data={documents}
                  keyExtractor={keyExtractor}
                  initialNumToRender={initialNumToRender}
                  renderItem={flatListRenderItem}
                />
              </Iffy>
              {/* Document Rows Tablet */}
              <Iffy is={!isDesktop}>
                <RecyclerListView
                  style={S.flex}
                  layoutProvider={layoutProvider}
                  dataProvider={dataProvider!}
                  rowRenderer={recylerListViewRenderItem}
                  renderAheadOffset={dimensions.height}
                  extendedState={tabletExtendedState}
                  forceNonDeterministicRendering
                  testID="documentlist-mobile-recyclerlistview"
                />
              </Iffy>
            </DNABox>
          </GridList>
        </ConditionalScrollView>
      </Iffy>
    </DNABox>
  )
}

const DNADocumentListPaginated: React.FC<Omit<DNADocumentListProps, 'documents'>> = (props) => {
  const { pagedRecords } = usePagination()

  return (
    <DNADocumentGridList
      {...props}
      documents={pagedRecords}
    />
  )
}

DNADocumentGridList.Paginated = DNADocumentListPaginated
export default DNADocumentGridList
